import { useContext } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { UserContext } from 'contexts/user';
import { AccountIcon } from 'components/account/AccountIcons';
import { CommunityFilter } from 'components/menuV2/NavCommunityFilters';
import NavCommunitySearch from 'components/menuV2/NavCommunitySearch';
import NavProfileBanner from 'components/menuV2/NavProfileBanner';
import featureFlags from 'lib/featureFlags';
import { getCommunityPath } from 'lib/community';
import { insertIf, join } from 'lib/helpers';
import { CommunityUserProfile } from 'types/community';
import { AccountMenu, PlacesMenu } from './HeaderMenus';
import styles from './SiteHeader.module.css';

type Props = {
  navTitle: string;
  showCommunitySearch?: boolean;
  communitySearchValue?: string;
  showCommunityFilters?: boolean;
  selectedCommunityFilter?: CommunityFilter;
  showProfileBanner?: boolean;
  profile?: CommunityUserProfile;
};

/** Site-wide header for the site refresh; replaces menuV2's Menu. */
export default function SiteHeader({
  navTitle,
  showCommunitySearch = false,
  communitySearchValue,
  showCommunityFilters = false,
  selectedCommunityFilter,
  showProfileBanner = false,
  profile,
}: Props) {
  const { user } = useContext(UserContext);

  return (
    <header className={styles.Header}>
      <div className={styles.Brand}>
        <a href="/" aria-label="Rive home" className={styles.Logo}>
          <Image src="/static/r_mark_white.svg" alt="" width={24} height={24} />
        </a>
        {navTitle.length > 0 && (
          <>
            <span className={styles.Divider} aria-hidden="true" />
            <PlacesMenu navTitle={navTitle} />
          </>
        )}
      </div>

      {showCommunityFilters && (
        <MarketplaceTabs selected={selectedCommunityFilter} />
      )}

      {showCommunitySearch && featureFlags.communitySearch() && (
        <label className={styles.Search}>
          <AccountIcon name="search" strokeWidth={2} />
          <NavCommunitySearch
            prefillValue={communitySearchValue}
            className={styles.SearchInput}
          />
        </label>
      )}

      {showProfileBanner && profile && <NavProfileBanner profile={profile} />}

      <div className={styles.End}>
        {user?.signedIn ? (
          <>
            <a
              href={process.env.NEXT_PUBLIC_EDITOR}
              target="_blank"
              rel="noopener noreferrer"
              className={join([styles.Button, styles.OpenRive])}
            >
              Open Rive
            </a>
            <AccountMenu user={user} />
          </>
        ) : (
          <a
            href={process.env.NEXT_PUBLIC_EDITOR}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.Button}
          >
            Get started
          </a>
        )}
      </div>
    </header>
  );
}

const marketplaceTabs: { value: string; label: string; path: string }[] = [
  { value: 'featured', label: 'Featured', path: 'featured' },
  { value: 'latest', label: 'Latest', path: 'latest' },
  { value: 'for-hire', label: 'For hire', path: 'for-hire' },
];

// Without search, the Marketplace browses by these tags instead.
const tagTabs = ['Bones', 'Characters', 'Icons', 'Loaders'].map((tag) => ({
  value: tag,
  label: tag,
  path: `/tag/${tag}`,
}));

function MarketplaceTabs({ selected }: { selected?: string }) {
  const tabs = [
    ...marketplaceTabs,
    ...(featureFlags.communitySearch() ? [] : tagTabs),
  ];

  return (
    <nav
      aria-label="Marketplace"
      className={styles.Tabs}
      data-testid="community-filters"
    >
      {tabs.map((tab) => (
        <Link
          key={tab.value}
          href={getCommunityPath(tab.path)}
          aria-current={tab.value === selected ? 'page' : undefined}
          className={join([
            styles.Tab,
            ...insertIf(tab.value === selected, styles.TabCurrent),
          ])}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
